import { z } from 'zod';
import type { FilterSpec, NormalizedRunV2, QuerySpec } from './types.js';

const shortString = z.string().max(256);
const identifier = z.string().max(512);
const finiteNumber = z.number().finite();
const boundedCount = z.number().finite().min(0).max(1_000_000_000_000_000);

const normalizedCardSchema = z.object({
  id: identifier,
  floorAdded: finiteNumber.min(0).max(10_000),
  upgradeLevel: finiteNumber.min(0).max(100)
});

const normalizedRelicSchema = z.object({
  id: identifier,
  floorAdded: finiteNumber.min(0).max(10_000)
});

const normalizedPlayerSchema = z.object({
  id: finiteNumber.int().min(0).max(128),
  character: shortString,
  deck: z.array(normalizedCardSchema).max(2_000),
  relics: z.array(normalizedRelicSchema).max(1_000),
  potions: z.array(identifier).max(128),
  badges: z.array(identifier).max(256),
  maxPotionSlots: finiteNumber.int().min(0).max(64)
});

const localizedTitleSchema = z.object({ table: shortString, key: identifier });
const timelineCardSchema = z.object({ id: identifier, floorAdded: finiteNumber.min(0).max(10_000), upgradeLevel: finiteNumber.min(0).max(100) });
const timelinePointSchema = z.object({
  floor: finiteNumber.min(0).max(10_000),
  act: finiteNumber.min(0).max(100),
  actFloor: finiteNumber.min(0).max(10_000),
  type: shortString,
  label: identifier,
  ancientId: identifier.nullable().optional(),
  hp: finiteNumber,
  maxHp: finiteNumber,
  gold: finiteNumber,
  damageTaken: finiteNumber,
  hpHealed: finiteNumber,
  goldGained: finiteNumber,
  goldSpent: finiteNumber,
  goldLost: finiteNumber.optional(),
  goldStolen: finiteNumber.optional(),
  stolenLoot: finiteNumber.optional(),
  maxHpGained: finiteNumber.optional(),
  maxHpLost: finiteNumber.optional(),
  affectedByFurCoat: z.boolean().optional(),
  turns: finiteNumber.optional(),
  cardsGained: z.array(timelineCardSchema).max(512).optional(),
  cardsRemoved: z.array(timelineCardSchema).max(512).optional(),
  cardsTransformed: z.array(z.object({ from: timelineCardSchema.nullable().optional(), to: timelineCardSchema.nullable().optional() })).max(512).optional(),
  upgradedCards: z.array(identifier).max(512).optional(),
  downgradedCards: z.array(identifier).max(512).optional(),
  enchantedCards: z.array(z.object({ card: timelineCardSchema.nullable().optional(), enchantment: identifier, amount: finiteNumber })).max(512).optional(),
  cardChoices: z.array(z.object({ id: identifier, picked: z.boolean(), upgradeLevel: finiteNumber.min(0).max(100) })).max(512).optional(),
  relicChoices: z.array(z.object({ id: identifier, picked: z.boolean() })).max(512).optional(),
  eventChoices: z.array(z.object({ title: localizedTitleSchema })).max(512).optional(),
  ancientChoices: z.array(z.object({ relicId: identifier, title: localizedTitleSchema, chosen: z.boolean() })).max(512).optional(),
  potionChoices: z.array(z.object({ id: identifier, picked: z.boolean() })).max(512).optional(),
  potionsUsed: z.array(identifier).max(128).optional(),
  potionsBought: z.array(identifier).max(128).optional(),
  potionsDiscarded: z.array(identifier).max(128).optional(),
  boughtRelics: z.array(identifier).max(512).optional(),
  boughtColorless: z.array(identifier).max(512).optional(),
  relicsRemoved: z.array(identifier).max(512).optional(),
  completedQuests: z.array(identifier).max(512).optional(),
  restChoices: z.array(shortString).max(512).optional()
});

const characterProgressSchema = z.object({
  character: shortString,
  wins: boundedCount,
  losses: boundedCount,
  playtime: boundedCount.optional().default(0),
  fastestWinTime: boundedCount.optional().default(0),
  bestWinStreak: boundedCount.optional().default(0),
  currentStreak: boundedCount.optional().default(0),
  maxAscension: finiteNumber.min(0).max(100).optional().default(0),
  preferredAscension: finiteNumber.min(0).max(100).optional().default(0),
  badges: z.array(z.object({ id: identifier, rarity: shortString, count: boundedCount })).max(1_000).optional().default([])
});

const entityProgressSchema = z.object({ id: identifier, picked: boundedCount.optional(), skipped: boundedCount.optional(), wins: boundedCount.optional(), losses: boundedCount.optional() });
const encounterProgressSchema = z.object({
  id: identifier,
  characters: z.array(z.object({ character: shortString, wins: boundedCount, losses: boundedCount })).max(128).default([])
});

export const careerProgressSchema = z.object({
  characterStats: z.array(characterProgressSchema).max(128).default([]),
  cardStats: z.array(entityProgressSchema).max(20_000).default([]),
  encounterStats: z.array(encounterProgressSchema).max(10_000).default([]),
  enemyStats: z.array(encounterProgressSchema).max(10_000).default([]),
  ancientStats: z.array(encounterProgressSchema).max(10_000).default([]),
  epochs: z.array(z.object({ id: identifier, obtainDate: boundedCount, state: shortString })).max(10_000).default([]),
  discoveries: z.record(shortString, z.array(identifier).max(50_000)).default({}),
  totalPlaytime: boundedCount.default(0),
  floorsClimbed: boundedCount.default(0),
  totalUnlocks: boundedCount.default(0),
  architectDamage: boundedCount.optional().default(0),
  currentScore: boundedCount.optional().default(0),
  maxMultiplayerAscension: finiteNumber.min(0).max(100).optional().default(0),
  preferredMultiplayerAscension: finiteNumber.min(0).max(100).optional().default(0),
  testSubjectKills: boundedCount.optional().default(0),
  wongoPoints: boundedCount.optional().default(0),
  unlockedAchievements: z.array(identifier).max(10_000).optional().default([]),
  rawVersion: finiteNumber.min(0).max(1_000_000).optional().default(0)
});

export const cloudNormalizedRunSchema = z.object({
  modelVersion: z.literal(2),
  parserVersion: shortString,
  sourceScope: z.literal('history'),
  sourceKey: identifier,
  importedAt: boundedCount,
  capabilities: z.object({
    hasMap: z.boolean(), hasPlayerTelemetry: z.boolean(), hasCardChoices: z.boolean(),
    hasRelicEvents: z.boolean(), hasEncounterEvents: z.boolean(), hasVersion: z.boolean()
  }),
  id: identifier,
  fileName: identifier,
  seed: z.literal(''),
  buildId: shortString,
  gameMode: shortString,
  schemaVersion: finiteNumber,
  platformType: shortString,
  acts: z.array(shortString).max(128).default([]),
  modifiers: z.array(shortString).max(1_000).default([]),
  players: z.array(normalizedPlayerSchema).min(1).max(16),
  playerCount: finiteNumber.int().min(1).max(16),
  isMultiplayer: z.boolean(),
  character: shortString,
  ascension: finiteNumber.min(0).max(100),
  win: z.boolean(),
  status: z.enum(['win', 'loss', 'abandoned']),
  floor: finiteNumber.min(0).max(10_000),
  runTime: boundedCount,
  startTime: boundedCount,
  deckSize: finiteNumber.min(0).max(2_000),
  deck: z.array(normalizedCardSchema).max(2_000),
  relics: z.array(normalizedRelicSchema).max(1_000),
  relicCount: finiteNumber.min(0).max(1_000),
  gold: finiteNumber,
  finalHp: finiteNumber,
  maxHp: finiteNumber,
  totalDamageTaken: finiteNumber,
  eliteCount: finiteNumber.min(0).max(10_000),
  combatCount: finiteNumber.min(0).max(10_000),
  upgradeCount: finiteNumber.min(0).max(2_000),
  killedBy: identifier.nullable(),
  map: z.array(z.never()).max(0),
  timeline: z.array(timelinePointSchema).max(10_000),
  playerTimelines: z.array(z.array(timelinePointSchema).max(10_000)).max(16).optional(),
  cardChoices: z.array(z.object({ floor: finiteNumber.min(0).max(10_000), offered: z.array(identifier).max(512), picked: identifier.nullable() })).max(10_000),
  encounterEvents: z.array(z.object({ id: identifier, floor: finiteNumber.min(0).max(10_000), type: shortString.optional(), act: finiteNumber.optional(), damageTaken: finiteNumber.optional(), turns: finiteNumber.optional(), killedPlayer: z.boolean().optional() })).max(10_000),
  relicEvents: z.array(identifier).max(10_000),
  actCount: finiteNumber.min(0).max(100).optional().default(0),
  restChoices: z.array(z.object({ floor: finiteNumber, act: finiteNumber, choice: shortString })).max(10_000).optional().default([]),
  damageSources: z.array(z.object({ floor: finiteNumber, act: finiteNumber, type: shortString, damageTaken: finiteNumber })).max(10_000).optional().default([]),
  raw: z.object({}).default({})
});

export const runStatusSchema = z.enum(['win', 'loss', 'abandoned']);

export const filterSpecSchema = z.object({
  mode: z.array(z.string()).default([]),
  characters: z.array(z.string()).default([]),
  builds: z.array(z.string()).default([]),
  ascensions: z.array(z.number().int().min(0).max(99)).default([]),
  party: z.enum(['all', 'solo', 'coop']).default('all'),
  outcomes: z.array(runStatusSchema).default([]),
  dateFrom: z.string().nullable().default(null),
  dateTo: z.string().nullable().default(null),
  minDuration: z.number().nonnegative().nullable().default(null),
  maxDuration: z.number().nonnegative().nullable().default(null),
  includeCards: z.array(z.string()).default([]),
  excludeCards: z.array(z.string()).default([]),
  includeRelics: z.array(z.string()).default([]),
  excludeRelics: z.array(z.string()).default([])
});

export const normalizedRunV2Schema = z.object({
  modelVersion: z.literal(2),
  parserVersion: z.string(),
  sourceScope: z.enum(['history', 'imported', 'demo']),
  sourceKey: z.string(),
  importedAt: z.number(),
  capabilities: z.object({
    hasMap: z.boolean(),
    hasPlayerTelemetry: z.boolean(),
    hasCardChoices: z.boolean(),
    hasRelicEvents: z.boolean(),
    hasEncounterEvents: z.boolean(),
    hasVersion: z.boolean()
  }),
  id: z.string(),
  fileName: z.string(),
  seed: z.string(),
  buildId: z.string(),
  gameMode: z.string(),
  schemaVersion: z.number(),
  platformType: z.string(),
  players: z.array(z.looseObject({ id: z.number(), character: z.string() })),
  playerCount: z.number(),
  isMultiplayer: z.boolean(),
  character: z.string(),
  ascension: z.number(),
  win: z.boolean(),
  status: runStatusSchema,
  floor: z.number(),
  runTime: z.number(),
  startTime: z.number(),
  deckSize: z.number(),
  deck: z.array(z.looseObject({ id: z.string() })),
  relics: z.array(z.looseObject({ id: z.string() })),
  relicCount: z.number(),
  gold: z.number(),
  finalHp: z.number(),
  maxHp: z.number(),
  totalDamageTaken: z.number(),
  eliteCount: z.number(),
  combatCount: z.number(),
  upgradeCount: z.number(),
  killedBy: z.string().nullable(),
  map: z.array(z.record(z.string(), z.unknown())),
  timeline: z.array(z.looseObject({ floor: z.number() })),
  cardChoices: z.array(z.looseObject({ offered: z.array(z.string()) })),
  encounterEvents: z.array(z.looseObject({ id: z.string() })),
  relicEvents: z.array(z.string()),
  raw: z.record(z.string(), z.unknown())
}).loose();

export const querySpecSchema = z.object({
  id: z.string(),
  dataSource: z.enum(['runs', 'cards', 'relics', 'encounters', 'floors', 'players', 'career', 'imported']),
  metricIds: z.array(z.string()).min(1),
  dimensionIds: z.array(z.string()).max(2),
  filter: filterSpecSchema,
  cohorts: z.array(z.object({ id: z.enum(['A', 'B']), name: z.string(), filter: filterSpecSchema })).optional(),
  sort: z.array(z.object({ field: z.string(), direction: z.enum(['asc', 'desc']) })),
  limit: z.number().int().min(1).max(500),
  visualization: z.enum(['kpi', 'table', 'line', 'area', 'bar', 'stacked-bar', 'scatter', 'histogram', 'boxplot', 'heatmap']),
  timeWindow: z.number().int().positive().nullable().optional(),
  focus: z.object({ kind: z.enum(['card', 'relic', 'encounter', 'ancient', 'run', 'character', 'build', 'ascension', 'outcome', 'party', 'gameMode', 'floor', 'act', 'date', 'week', 'playerPosition', 'roomType']), id: identifier, label: shortString.optional() }).optional(),
  focuses: z.array(z.object({ kind: z.enum(['card', 'relic', 'encounter', 'ancient', 'run', 'character', 'build', 'ascension', 'outcome', 'party', 'gameMode', 'floor', 'act', 'date', 'week', 'playerPosition', 'roomType']), id: identifier, label: shortString.optional() })).max(12).optional(),
  cardCategory: z.enum(['all', 'colorless', 'Ironclad', 'Silent', 'Regent', 'Necrobinder', 'Defect']).optional()
});

const preferenceScopeSchema = z.object({
  source: z.enum(['ancient', 'card']).optional(),
  ancientId: identifier.optional(),
  playerCharacter: shortString.optional(),
  cardCategory: z.enum(['all', 'colorless', 'Ironclad', 'Silent', 'Regent', 'Necrobinder', 'Defect']).optional(),
  focusItemId: identifier.optional()
});

export const overallAnalyzeRequestSchema = z.discriminatedUnion('operation', [
  z.object({ operation: z.literal('query'), query: querySpecSchema }),
  z.object({ operation: z.literal('entity-profile'), kind: z.enum(['card', 'relic', 'encounter']), id: identifier, filter: filterSpecSchema, perspective: shortString.optional() }),
  z.object({ operation: z.literal('preference-arena'), filter: filterSpecSchema, scope: preferenceScopeSchema.optional() }),
  z.object({ operation: z.literal('card-archetypes'), filter: filterSpecSchema, characterId: shortString }),
  z.object({ operation: z.literal('insights'), filter: filterSpecSchema })
]);

export const EMPTY_FILTER: FilterSpec = filterSpecSchema.parse({});

export function validateNormalizedRun(value: unknown): NormalizedRunV2 {
  return normalizedRunV2Schema.parse(value) as unknown as NormalizedRunV2;
}

export function validateQuery(value: unknown): QuerySpec {
  return querySpecSchema.parse(value) as QuerySpec;
}
